import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp, Users, Zap } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function CaseStudies() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const caseStudies = [
    {
      title: "Local Retailer Increases Online Sales by 340%",
      industry: "E-Commerce",
      challenge: "A family-owned retail business wanted to expand online but their outdated website wasn't converting visitors. They were losing customers to competitors with better online experiences.",
      solution: "We built a modern e-commerce website with intuitive navigation, fast checkout, and mobile optimization. Integrated inventory management and automated email marketing to nurture leads.",
      results: [
        { metric: "340%", label: "Increase in Online Sales" },
        { metric: "65%", label: "Higher Conversion Rate" },
        { metric: "4.2x", label: "More Mobile Orders" },
      ],
      testimonial: "The new website transformed our business. We're now getting more online orders than in-store purchases, and our revenue has tripled in just six months.",
      icon: TrendingUp,
    },
    {
      title: "Healthcare Provider Reduces No-Shows by 78%",
      industry: "Healthcare",
      challenge: "A medical practice was losing thousands of dollars monthly due to missed appointments. Their manual reminder system wasn't working, and staff spent hours on the phone confirming appointments.",
      solution: "Implemented an automated appointment scheduling and reminder system with SMS and email notifications. Added online booking capability and integrated with their existing practice management software.",
      results: [
        { metric: "78%", label: "Reduction in No-Shows" },
        { metric: "15hrs", label: "Staff Time Saved Weekly" },
        { metric: "$45K", label: "Annual Revenue Recovered" },
      ],
      testimonial: "This system paid for itself in the first month. Our staff can focus on patient care instead of chasing down appointments, and our schedule is consistently full.",
      icon: Users,
    },
    {
      title: "Manufacturing Company Cuts Processing Time by 60%",
      industry: "Manufacturing",
      challenge: "A growing manufacturer was drowning in paperwork. Order processing took days, errors were common, and they couldn't scale without hiring more administrative staff.",
      solution: "Built a custom order management system that automated data entry, integrated with their accounting software, and provided real-time visibility into order status. Added mobile app for field sales team.",
      results: [
        { metric: "60%", label: "Faster Order Processing" },
        { metric: "92%", label: "Reduction in Errors" },
        { metric: "Zero", label: "Additional Admin Hires Needed" },
      ],
      testimonial: "We doubled our order volume without adding staff. The system handles everything automatically, and we can see exactly where every order is at any moment.",
      icon: Zap,
    },
    {
      title: "Real Estate Agency Closes 40% More Deals",
      industry: "Real Estate",
      challenge: "A real estate team was losing leads because of slow follow-up and disorganized client information. They needed a better way to manage relationships and track the sales pipeline.",
      solution: "Developed a custom CRM tailored to their unique sales process. Automated lead distribution, follow-up reminders, and client communication. Integrated with their MLS system and marketing tools.",
      results: [
        { metric: "40%", label: "More Deals Closed" },
        { metric: "3x", label: "Faster Lead Response" },
        { metric: "85%", label: "Lead Follow-Up Rate" },
      ],
      testimonial: "This CRM was built specifically for how we work. We never lose track of a lead, and our agents are closing more deals than ever before.",
      icon: TrendingUp,
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="text-gradient">Case Studies</span>
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Real businesses. Real challenges. Real results. See how we've helped companies like yours achieve their goals.
            </motion.p>
          </div>
        </section>

        {/* Case Studies */}
        <section className="py-20 px-4">
          <div className="container max-w-5xl mx-auto space-y-20">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.title}
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="p-8 rounded-2xl bg-card border border-border">
                  {/* Header */}
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                      <study.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold mb-2">{study.title}</h2>
                      <p className="text-muted-foreground">{study.industry}</p>
                    </div>
                  </div>

                  {/* Challenge */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-3">The Challenge</h3>
                    <p className="text-muted-foreground leading-relaxed">{study.challenge}</p>
                  </div>

                  {/* Solution */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-3">Our Solution</h3>
                    <p className="text-muted-foreground leading-relaxed">{study.solution}</p>
                  </div>

                  {/* Results */}
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold mb-4">The Results</h3>
                    <div className="grid md:grid-cols-3 gap-6">
                      {study.results.map((result) => (
                        <div key={result.label} className="text-center p-4 rounded-lg bg-muted/50">
                          <div className="text-3xl font-bold text-gradient mb-1">{result.metric}</div>
                          <div className="text-sm text-muted-foreground">{result.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className="p-6 rounded-lg bg-muted/30 border-l-4 border-primary">
                    <p className="text-lg italic text-muted-foreground">"{study.testimonial}"</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's discuss how we can help you achieve results like these.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-primary to-secondary text-white"
            >
              Start Your Project <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
